import ExcelJS from 'exceljs'
import * as XLSX from 'xlsx'

export type FichaExportIngrediente = {
  codigo: string
  descricao: string
  qtdG: number
  energiaKcal: number
  proteinaG: number
  lipideosG: number
  carboidratoG: number
  calcioMg: number
  ferroMg: number
  retinolMcg: number
  vitaminaCMg: number
}

export type FichaExportPayload = {
  modalidadeLabel: string
  faixaEtaria?: string
  nomePreparacao: string
  ingredientes: FichaExportIngrediente[]
}

const HEADER = [
  'Código Do Alimento',
  'Descrição do Alimento',
  'QTD (g)',
  'Energia (Kcal)',
  'Medida caseira',
  'PB (g)',
  'PL (g)',
  'FC',
  'Proteína (g)',
  'Lipídeos (g)',
  'Carboidrato (g)',
  'Cálcio (mg)',
  'Ferro (mg)',
  'Retinol (mcg)',
  'Vitamina C (mg)',
] as const

const COLS = 15

/** Verde sistema (título / cabeçalho) */
const FILL_DARK = 'FF2E7D32'
/** Verde planilha oficial (#E2F0D9) */
const FILL_LIGHT = 'FFE2F0D9'
const FONT_WHITE = 'FFFFFFFF'
const FONT_DARK = 'FF132615'
const BORDER_COLOR = 'FFCFE0CF'

function round2(n: number): number {
  return Math.round((Number(n) || 0) * 100) / 100
}

function sanitizeSheetName(name: string, used: Set<string>): string {
  let base = (name || 'Ficha')
    .replace(/[\\/*?[\]:]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 31)
  if (!base) base = 'Ficha'
  let candidate = base
  let i = 2
  while (used.has(candidate.toLowerCase())) {
    const suffix = ` (${i})`
    candidate = `${base.slice(0, Math.max(1, 31 - suffix.length))}${suffix}`
    i += 1
  }
  used.add(candidate.toLowerCase())
  return candidate
}

function ingredientRow(ing: FichaExportIngrediente): (string | number)[] {
  return [
    ing.codigo || '',
    ing.descricao || '-',
    round2(ing.qtdG),
    round2(ing.energiaKcal),
    '',
    '',
    '',
    '',
    round2(ing.proteinaG),
    round2(ing.lipideosG),
    round2(ing.carboidratoG),
    round2(ing.calcioMg),
    round2(ing.ferroMg),
    round2(ing.retinolMcg),
    round2(ing.vitaminaCMg),
  ]
}

export function buildSheetRows(ficha: FichaExportPayload): (string | number)[][] {
  const ings = ficha.ingredientes
  const tot = ings.reduce(
    (acc, ing) => ({
      qtdG: acc.qtdG + (Number(ing.qtdG) || 0),
      energiaKcal: acc.energiaKcal + (Number(ing.energiaKcal) || 0),
      proteinaG: acc.proteinaG + (Number(ing.proteinaG) || 0),
      lipideosG: acc.lipideosG + (Number(ing.lipideosG) || 0),
      carboidratoG: acc.carboidratoG + (Number(ing.carboidratoG) || 0),
      calcioMg: acc.calcioMg + (Number(ing.calcioMg) || 0),
      ferroMg: acc.ferroMg + (Number(ing.ferroMg) || 0),
      retinolMcg: acc.retinolMcg + (Number(ing.retinolMcg) || 0),
      vitaminaCMg: acc.vitaminaCMg + (Number(ing.vitaminaCMg) || 0),
    }),
    {
      qtdG: 0,
      energiaKcal: 0,
      proteinaG: 0,
      lipideosG: 0,
      carboidratoG: 0,
      calcioMg: 0,
      ferroMg: 0,
      retinolMcg: 0,
      vitaminaCMg: 0,
    },
  )

  const kcal = tot.energiaKcal || 0
  const kcalPtn = tot.proteinaG * 4
  const kcalLip = tot.lipideosG * 9
  const kcalCho = tot.carboidratoG * 4
  const fracCho = kcal > 0 ? kcalCho / kcal : 0
  const fracLip = kcal > 0 ? kcalLip / kcal : 0
  const fracPtn = kcal > 0 ? kcalPtn / kcal : 0

  const faixa = ficha.faixaEtaria?.trim() || ''
  const rows: (string | number)[][] = [
    ['FICHA TÉCNICA DE PREPARAÇÃO'],
    [
      `MODALIDADE DE ENSINO: ${ficha.modalidadeLabel || ''}`.trimEnd() +
        `                                        FAIXA ETÁRIA: ${faixa || ' a  ANOS'}`,
    ],
    [`NOME DA PREPARAÇÃO: ${ficha.nomePreparacao || ''}`],
    [...HEADER],
  ]

  // Totais logo abaixo do último ingrediente (como no ARROZ BRANCO oficial — sem linhas vazias).
  rows.push(...ings.map(ingredientRow))

  // Soma da Energia (col. D) logo abaixo dos ingredientes — sem duplicar em A.
  rows.push([
    '',
    '',
    '',
    round2(tot.energiaKcal),
    '',
    '',
    '',
    '',
    round2(tot.proteinaG),
    round2(tot.lipideosG),
    round2(tot.carboidratoG),
    round2(tot.calcioMg),
    round2(tot.ferroMg),
    round2(tot.retinolMcg),
    round2(tot.vitaminaCMg),
  ])
  rows.push(['', '', '', '', '', '', '', '', round2(kcalPtn), round2(kcalLip), round2(kcalCho)])
  rows.push([
    `Rendimento da porção: ${tot.qtdG > 0 ? `${round2(tot.qtdG)}g` : ''}`,
    '',
    '',
    'MODO DE PREPARO:',
  ])
  rows.push(['Medida caseira: '])
  rows.push(['Kcal p/porção', round2(tot.energiaKcal)])
  rows.push(['Carboidratos', round2(fracCho)])
  rows.push(['Lipideos ', round2(fracLip)])
  rows.push(['Proteinas', round2(fracPtn), '', 'TEMPO DE PREPARO: '])
  rows.push(['', '', '', '', ' Nutricionista CRN nº\n_____________________________________________'])

  return rows
}

/** Mantido para testes com sheetjs (sem estilo). */
export function buildFichaTecnicaWorkbook(fichas: FichaExportPayload[]): XLSX.WorkBook {
  const wb = XLSX.utils.book_new()
  const used = new Set<string>()
  const list = fichas.length ? fichas : [{ modalidadeLabel: '', nomePreparacao: 'Vazia', ingredientes: [] }]
  for (const ficha of list) {
    const name = sanitizeSheetName(ficha.nomePreparacao || ficha.modalidadeLabel || 'Ficha', used)
    const ws = XLSX.utils.aoa_to_sheet(buildSheetRows(ficha))
    ws['!cols'] = HEADER.map((_, i) => ({ wch: i === 1 ? 36 : i === 0 ? 14 : 12 }))
    XLSX.utils.book_append_sheet(wb, ws, name)
  }
  return wb
}

function solidFill(argb: string): ExcelJS.Fill {
  return { type: 'pattern', pattern: 'solid', fgColor: { argb } }
}

function thinBorder(): Partial<ExcelJS.Borders> {
  const edge: Partial<ExcelJS.Border> = { style: 'thin', color: { argb: BORDER_COLOR } }
  return { top: edge, left: edge, bottom: edge, right: edge }
}

const FONT_NAME = 'Arial'

function paintRow(
  sheet: ExcelJS.Worksheet,
  rowIndex: number,
  fillArgb: string,
  opts?: {
    bold?: boolean
    white?: boolean
    size?: number
    align?: 'left' | 'center' | 'right'
    vertical?: 'top' | 'middle' | 'bottom'
  },
) {
  const row = sheet.getRow(rowIndex)
  for (let c = 1; c <= COLS; c++) {
    const cell = row.getCell(c)
    cell.fill = solidFill(fillArgb)
    cell.border = thinBorder()
    cell.font = {
      bold: !!opts?.bold,
      color: { argb: opts?.white ? FONT_WHITE : FONT_DARK },
      name: FONT_NAME,
      size: opts?.size ?? 10,
    }
    cell.alignment = {
      horizontal: opts?.align ?? 'left',
      vertical: opts?.vertical ?? 'middle',
      wrapText: true,
    }
  }
}

/** Colunas numéricas do MODELO (1-based): QTD, Energia, PB, PL, FC, macros… */
const NUMERIC_COLS = new Set([3, 4, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15])

function writeStyledSheet(workbook: ExcelJS.Workbook, ficha: FichaExportPayload, sheetName: string) {
  const rows = buildSheetRows(ficha)
  const sheet = workbook.addWorksheet(sheetName, {
    views: [{ showGridLines: false }],
  })
  // Larguras próximas da planilha oficial (código estreito, descrição larga, nutrientes compactos)
  sheet.columns = [
    { width: 12 },
    { width: 28 },
    { width: 9 },
    { width: 11 },
    { width: 14 },
    { width: 8 },
    { width: 8 },
    { width: 6 },
    { width: 10 },
    { width: 10 },
    { width: 11 },
    { width: 10 },
    { width: 9 },
    { width: 11 },
    { width: 11 },
  ]

  rows.forEach((rowValues, idx) => {
    const excelRow = sheet.getRow(idx + 1)
    rowValues.forEach((v, ci) => {
      excelRow.getCell(ci + 1).value = v as ExcelJS.CellValue
    })
    for (let c = rowValues.length + 1; c <= COLS; c++) {
      if (!excelRow.getCell(c).value) excelRow.getCell(c).value = ''
    }
  })

  const headerRow = 4
  const nIng = ficha.ingredientes.length
  const firstIng = 5
  const lastIng = nIng > 0 ? 4 + nIng : 4
  const totalsRow = lastIng + 1
  const energyShareRow = totalsRow + 1
  const footerStart = energyShareRow + 1
  const footerEnd = rows.length

  // Título / metadados / cabeçalho — Arial, centralizado como no modelo
  paintRow(sheet, 1, FILL_DARK, {
    bold: true,
    white: true,
    size: 14,
    align: 'center',
    vertical: 'middle',
  })
  paintRow(sheet, 2, 'FFFFFFFF', { size: 10, align: 'left', vertical: 'middle' })
  paintRow(sheet, 3, FILL_LIGHT, { bold: true, size: 11, align: 'left', vertical: 'middle' })
  paintRow(sheet, headerRow, FILL_DARK, {
    bold: true,
    white: true,
    size: 9,
    align: 'center',
    vertical: 'middle',
  })

  if (nIng > 0) {
    for (let r = firstIng; r <= lastIng; r++) {
      const row = sheet.getRow(r)
      row.height = 16
      for (let c = 1; c <= COLS; c++) {
        const cell = row.getCell(c)
        cell.border = thinBorder()
        cell.font = { name: FONT_NAME, size: 10, color: { argb: FONT_DARK } }
        cell.alignment = {
          horizontal: NUMERIC_COLS.has(c) ? 'center' : c === 2 ? 'left' : 'center',
          vertical: 'middle',
          wrapText: true,
        }
        if (NUMERIC_COLS.has(c) && typeof cell.value === 'number') {
          cell.numFmt = '0.00'
        }
      }
    }
  }

  paintRow(sheet, totalsRow, FILL_LIGHT, { bold: true, size: 10, align: 'center' })
  paintRow(sheet, energyShareRow, FILL_LIGHT, { size: 10, align: 'center' })
  for (let r = footerStart; r <= footerEnd; r++) {
    paintRow(sheet, r, FILL_LIGHT, { size: 10, align: 'left', vertical: 'top' })
  }
  // Totais: números centralizados
  for (const r of [totalsRow, energyShareRow]) {
    const row = sheet.getRow(r)
    for (let c = 1; c <= COLS; c++) {
      if (NUMERIC_COLS.has(c) || c === 1) {
        row.getCell(c).alignment = { horizontal: 'center', vertical: 'middle', wrapText: true }
      }
      if (typeof row.getCell(c).value === 'number') {
        row.getCell(c).numFmt = '0.00'
      }
    }
  }

  sheet.mergeCells(1, 1, 1, COLS)
  sheet.mergeCells(2, 1, 2, COLS)
  sheet.mergeCells(3, 1, 3, COLS)
  sheet.getRow(1).height = 24
  sheet.getRow(2).height = 18
  sheet.getRow(3).height = 18
  sheet.getRow(headerRow).height = 32
}

export async function downloadFichasTecnicasXlsx(fichas: FichaExportPayload[], filename: string) {
  if (!fichas.length) throw new Error('Nenhuma ficha técnica para exportar.')
  const workbook = new ExcelJS.Workbook()
  workbook.creator = 'ISEN EDUCAE'
  const used = new Set<string>()
  for (const ficha of fichas) {
    const name = sanitizeSheetName(ficha.nomePreparacao || ficha.modalidadeLabel || 'Ficha', used)
    writeStyledSheet(workbook, ficha, name)
  }
  const buffer = await workbook.xlsx.writeBuffer()
  const safeName = filename.toLowerCase().endsWith('.xlsx') ? filename : `${filename}.xlsx`
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = safeName
  a.click()
  URL.revokeObjectURL(url)
}
